/**
 * @fileoverview Aba "Acompanhamento": triagens da empresa (em andamento,
 * concluídas, canceladas), com filtro por situação e por triagem, e atalho
 * para abrir a conversa.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { ExternalLink } from "lucide-react";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";

const STATUS_FILTERS = ["active", "COMPLETED", "CANCELLED", "FAILED", "all"];
const s = (key, params) => i18n.t(`triage.manager.sessions.${key}`, params);

const LOCALES = { pt: "pt-BR", en: "en-US", es: "es-ES" };
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(LOCALES[(i18n.language || "pt").slice(0, 2)] || "pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const SessionsMonitor = ({ onNavigate }) => {
  const classes = useStyles();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();
  const [status, setStatus] = useState("active");
  const [definitionId, setDefinitionId] = useState("");
  const [definitions, setDefinitions] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ sessions: [], count: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  // Número da última busca: resposta de filtro antigo ou de "Carregar mais" pendente não entra.
  const requestRef = useRef(0);

  useEffect(() => {
    api
      .get("/triage-definitions")
      .then(({ data: defs }) => setDefinitions(Array.isArray(defs) ? defs : []))
      .catch(() => setDefinitions([]));
  }, []);

  const load = useCallback(
    async (nextPage) => {
      const current = ++requestRef.current;
      setLoading(true);
      try {
        const params = { page: nextPage, pageSize: 25 };
        if (status !== "all") params.status = status;
        if (definitionId) params.definitionId = definitionId;
        const { data: res } = await api.get("/triage-sessions", { params });
        if (current !== requestRef.current) return;
        setData((prev) => ({
          sessions: nextPage === 1 ? res.sessions : [...prev.sessions, ...res.sessions],
          count: res.count,
          hasMore: res.hasMore,
        }));
        setPage(nextPage);
      } catch (err) {
        if (current === requestRef.current) toastError(err);
      } finally {
        if (current === requestRef.current) setLoading(false);
      }
    },
    [status, definitionId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const abrir = (sessao) => {
    if (!sessao.ticketUuid) return;
    history.push(`/tickets/${sessao.ticketUuid}`);
    if (onNavigate) onNavigate();
  };

  const situacao = (st) => i18n.t(`triage.sessionStatus.${st}`);

  return (
    <>
      <div className={classes.filters}>
        <ModernSelect
          className={`${classes.field} ${classes.filter}`}
          size="small"
          label={s("status")}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_FILTERS.map((v) => (
            <MenuItem key={v} value={v}>
              {s(`statuses.${v}`)}
            </MenuItem>
          ))}
        </ModernSelect>
        <ModernSelect
          className={`${classes.field} ${classes.filter}`}
          size="small"
          label={s("columns.definition")}
          value={definitionId}
          onChange={(e) => setDefinitionId(e.target.value)}
          displayEmpty
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">{s("allDefinitions")}</MenuItem>
          {definitions.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </ModernSelect>
        {!loading && <span className={classes.count}>{s("total", { count: data.count })}</span>}
      </div>

      {loading && page === 1 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <CircularProgress size={24} />
        </div>
      ) : data.sessions.length === 0 ? (
        <div className={classes.empty}>{s("empty")}</div>
      ) : compact ? (
        <div className={classes.cards}>
          {data.sessions.map((x) => (
            <div key={x.id} className={classes.row} style={{ alignItems: "flex-start" }}>
              <div className={classes.rowMain} style={{ cursor: "default" }}>
                <div className={classes.rowTitle}>
                  {x.contactName || s("noContact")}
                  <span className={classes.chip}>{situacao(x.status)}</span>
                </div>
                <div className={classes.rowDesc}>{x.definitionName || "—"}</div>
                <div className={classes.rowMeta}>
                  {formatDateTime(x.createdAt)}
                  {x.endedAt ? ` → ${formatDateTime(x.endedAt)}` : ""}
                </div>
              </div>
              {x.ticketUuid && (
                <button type="button" className={classes.link} onClick={() => abrir(x)} aria-label={s("open")} title={s("open")}>
                  <ExternalLink />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <table className={classes.table}>
          <thead>
            <tr>
              <th>{s("columns.customer")}</th>
              <th>{s("columns.definition")}</th>
              <th>{s("columns.status")}</th>
              <th>{s("columns.started")}</th>
              <th>{s("columns.ended")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.sessions.map((x) => (
              <tr key={x.id}>
                <td>{x.contactName || <span className={classes.cellMuted}>{s("noContact")}</span>}</td>
                <td className={classes.cellMuted}>{x.definitionName || "—"}</td>
                <td>
                  <span className={classes.chip}>{situacao(x.status)}</span>
                </td>
                <td className={classes.cellMuted}>{formatDateTime(x.createdAt)}</td>
                <td className={classes.cellMuted}>{x.endedAt ? formatDateTime(x.endedAt) : "—"}</td>
                <td style={{ textAlign: "right" }}>
                  {x.ticketUuid && (
                    <button type="button" className={classes.link} onClick={() => abrir(x)}>
                      {s("open")} <ExternalLink />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data.hasMore && (
        <div className={classes.loadMore}>
          <button type="button" className={classes.btnSecondary} onClick={() => load(page + 1)} disabled={loading}>
            {loading ? <CircularProgress size={14} /> : s("loadMore")}
          </button>
        </div>
      )}
    </>
  );
};

export default SessionsMonitor;
